import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  useCreateStripeAccountMutation, 
  useCreateAccountSessionMutation,
  useGetAccountStatusQuery,
  useGetAccountBalanceQuery,
} from '../redux/apis/endpoints/paymentApi';
import { SUPPORTED_COUNTRIES } from '../utils/countryUtils';

const userLang = navigator.language || navigator.userLanguage;

export const useStripeConnect = (user, refetchUserData, userCountryFromSlice) => {
  const history = useHistory();
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStep, setVerificationStep] = useState('idle'); // 'idle', 'creating', 'preparing', 'redirecting'
  const [statusChecking, setStatusChecking] = useState(false);
  
  const [skipStatus, setSkipStatus] = useState(true);
  const [skipBalance, setSkipBalance] = useState(true);

  const [showCountryDialog, setShowCountryDialog] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState('US');

  // API Hooks
  const [createStripeAccount, { isLoading: isCreatingAccount }] = useCreateStripeAccountMutation();
  const [createAccountSession, { isLoading: isCreatingSession }] = useCreateAccountSessionMutation();

  const { 
    data: accountStatus,
    refetch: refetchStatus,
    isLoading: isStatusLoading,
    isFetching: isStatusFetching,
    isSuccess: isStatusSuccess,
    isError: isStatusError,
    error: statusError
  } = useGetAccountStatusQuery(null, { 
    skip: skipStatus || !user?.stripeAccount,
    refetchOnMountOrArgChange: true // Ensures fresh status
  });
  
  const {
    data: accountBalanceData,
    refetch: refetchBalance,
    isLoading: isBalanceLoading,
    isFetching: isBalanceFetching,
    isSuccess: isBalanceSuccess,
    isError: isBalanceError,
    error: balanceError
  } = useGetAccountBalanceQuery(null, {
    skip: skipBalance || !user?.stripeAccount || !accountStatus?.status?.chargesEnabled,
    refetchOnMountOrArgChange: true // Ensures fresh balance
  });
  
  // Effect to enable status/balance fetching when conditions are met
  useEffect(() => {
    if (user?.stripeAccount && skipStatus) {
      // console.log('[useStripeConnect] User has Stripe account, enabling status fetch:', user.stripeAccount);
      setSkipStatus(false);
    }
    if (user?.stripeAccount && accountStatus?.status?.chargesEnabled && skipBalance) {
      // console.log('[useStripeConnect] User has verified Stripe account, enabling balance fetch:', user.stripeAccount);
      setSkipBalance(false);
    }
  }, [user, accountStatus, skipStatus, skipBalance]);

  // Effect to handle redirect back from Stripe
  useEffect(() => {
    const checkStripeStatusOnReturn = async () => {
      const isStripeReturn = window.location.search.includes('stripe_completion') || 
                             window.location.search.includes('stripe_refresh');
      
      if (user?.stripeAccount && !statusChecking && isStripeReturn) {
        setIsVerifying(false);
        setVerificationStep('idle');
        setStatusChecking(true);

        if (window.location.search.includes('stripe_completion=success')) {
          toast.loading('Finalizing verification...', { id: 'stripe-status-hook' });
        } else if (window.location.search.includes('stripe_refresh=true')) {
          toast.loading('Refreshing verification status...', { id: 'stripe-status-hook' });
        }

        try {
          setSkipStatus(false); // Ensure status is fetched
          const statusResult = await refetchStatus();
          
          if (statusResult.data?.status?.chargesEnabled) {
            toast.success('Verification completed successfully!', { id: 'stripe-status-hook' });
            setSkipBalance(false); // Enable balance fetch
            await refetchBalance();
          } else if (statusResult.data?.status?.detailsSubmitted) {
            toast.success('Information submitted, verification in progress.', { id: 'stripe-status-hook' });
          } else {
            toast.success('Verification status updated.', { id: 'stripe-status-hook' });
          }
          
          if (window.history.replaceState) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }
          await refetchUserData(); // Refresh user data from parent
        } catch (err) {
          console.error('[useStripeConnect] Error checking verification status on return:', err);
          toast.error('Failed to update verification status.', { id: 'stripe-status-hook' });
        } finally {
          setStatusChecking(false);
        }
      }
    };
    checkStripeStatusOnReturn();
  }, [user, refetchStatus, refetchUserData, statusChecking, refetchBalance, history]);


  const getVerificationButtonText = useCallback(() => {
    if (verificationStep === 'creating') return userLang === 'zh-CN' ? '创建账户中...' : 'Creating Account...';
    if (verificationStep === 'preparing') return userLang === 'zh-CN' ? '准备验证...' : 'Preparing Verification...';
    if (verificationStep === 'redirecting') return userLang === 'zh-CN' ? '跳转至Stripe...' : 'Redirecting to Stripe...';
    if (accountStatus?.status?.chargesEnabled) return userLang === 'zh-CN' ? '更新账户' : 'Update Account';
    if (accountStatus?.status?.detailsSubmitted) return userLang === 'zh-CN' ? '继续认证' : 'Continue Verification';
    return userLang === 'zh-CN' ? '完成认证' : 'Complete Verification';
  }, [verificationStep, accountStatus]);

  const detectAndConfirmCountry = useCallback(() => {
    if (user?.country) {
      return Promise.resolve(user.country);
    }
    let countryCode = userCountryFromSlice?.code || 'US';
    const isSupported = SUPPORTED_COUNTRIES.some(c => c.code === countryCode);
    if (!isSupported) countryCode = 'US';
    
    setDetectedCountry(countryCode);
    
    return new Promise((resolve, reject) => {
      setShowCountryDialog(true);
      // These will be set on window by the calling component (Wallet.js)
      // and then picked up by the CountryConfirmDialog
      window.confirmStripeCountry = resolve; 
      window.cancelStripeCountrySelection = reject;
    });
  }, [user, userCountryFromSlice, setShowCountryDialog, setDetectedCountry]);

  const handleStripeVerification = useCallback(async () => {
    setIsVerifying(true);
    let stripeAccountIdToUse = user?.stripeAccount;

    try {
      if (!stripeAccountIdToUse) {
        const confirmedCountry = await detectAndConfirmCountry();
        setVerificationStep('creating');
        toast.loading('Creating Stripe Connect account...', { id: 'stripe-connect-hook' });
        
        const createResult = await createStripeAccount({ country: confirmedCountry }).unwrap();
        if (!createResult?.success || !createResult.accountId) {
          throw new Error(createResult?.message || 'Failed to create Stripe account.');
        }
        stripeAccountIdToUse = createResult.accountId;
        toast.success('Stripe account created.', { id: 'stripe-connect-hook' });
        await refetchUserData(); // Refresh user to get new stripeAccount ID
         // Short delay to allow DB propagation and refetchUserData to complete
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      setVerificationStep('preparing');
      toast.loading('Preparing verification process...', { id: 'stripe-connect-hook' });

      // Refetch user data again to ensure stripeAccountIdToUse is the latest from user object
      // This is particularly important if the account was just created.
      const freshUser = await refetchUserData().unwrap ? refetchUserData().unwrap() : user;
      stripeAccountIdToUse = freshUser?.stripeAccount || stripeAccountIdToUse;

      if (!stripeAccountIdToUse) {
          throw new Error('Stripe Account ID not available after creation/fetch.');
      }

      const sessionResult = await createAccountSession({ account: stripeAccountIdToUse }).unwrap();
      const redirectUrl = sessionResult?.client_secret || sessionResult?.url || sessionResult?.accountLink?.url;

      if (!redirectUrl) {
        throw new Error('Failed to create account session - no redirect URL.');
      }

      setVerificationStep('redirecting');
      toast.success('Redirecting to Stripe...', { id: 'stripe-connect-hook' });
      await new Promise(resolve => setTimeout(resolve, 800));
      window.location.href = redirectUrl;

    } catch (error) {
      console.error('[useStripeConnect] Error in Stripe verification flow:', error);
      toast.error(error.message || 'Verification process failed.', { id: 'stripe-connect-hook' });
      setVerificationStep('idle');
      setIsVerifying(false);
      // If country dialog was part of the error path, ensure it's closed.
      if (error.message !== 'Country selection cancelled') { // Avoid double toast if dialog cancelled
          setShowCountryDialog(false); 
      }
    }
  }, [user, createStripeAccount, createAccountSession, refetchUserData, detectAndConfirmCountry]);

  const handleManualStatusCheck = useCallback(async () => {
    if (!user?.stripeAccount) {
        toast.error("No Stripe account to check.", { id: 'status-check-hook'});
        return;
    }
    setStatusChecking(true);
    toast.loading('Checking verification status...', { id: 'status-check-hook' });
    try {
      setSkipStatus(false); // Ensure status is fetched
      const result = await refetchStatus();
      if (result.data?.status?.chargesEnabled) {
        setSkipBalance(false); // Enable balance fetch if verified
        await refetchBalance();
      }
      await refetchUserData();
      toast.success('Verification status updated.', { id: 'status-check-hook' });
    } catch (error) {
      console.error('[useStripeConnect] Error during manual status check:', error);
      toast.error('Failed to check verification status.', { id: 'status-check-hook' });
    } finally {
      setStatusChecking(false);
    }
  }, [user, refetchStatus, refetchBalance, refetchUserData]);
  
  const isLoading = isCreatingAccount || isCreatingSession || 
                    (isStatusLoading && skipStatus === false) || 
                    (isBalanceLoading && skipBalance === false) || 
                    statusChecking;

  return {
    // Account Status
    accountStatus,
    isStatusLoading: (isStatusLoading && !skipStatus) || (isStatusFetching && !skipStatus), // More accurate loading
    isStatusError,
    statusError,
    refetchStatus, // Expose for manual refresh if needed outside
    handleManualStatusCheck,

    // Account Balance
    accountBalanceData,
    isBalanceLoading: (isBalanceLoading && !skipBalance) || (isBalanceFetching && !skipBalance), // More accurate loading
    isBalanceError,
    balanceError,
    refetchBalance, // Expose for manual refresh

    // Verification Flow
    isVerifying,
    verificationStep,
    handleStripeVerification,
    getVerificationButtonText,
    
    // Country Dialog
    showCountryDialog,
    setShowCountryDialog,
    detectedCountry,
    // confirmCountry and cancelCountrySelection will be handled by Wallet via window object

    // Combined Loading
    isLoadingOverall: isLoading 
  };
}; 